import { useEffect, useState } from "react";
import api from "../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import PageHeader from "../components/PageHeader";
import {
  Save,
  Globe,
  KeyRound,
  Lock,
  Eye,
  EyeOff,
  Plug,
  CheckCircle2,
  XCircle,
  Loader2,
  Zap,
  CopyIcon,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const SHELL_BG = "#0a1119";
const PANEL_BG = "#111a26";
const PANEL_BORDER = "#1f2a3a";
const ACCENT = "#22c55e";

export default function ConfigDST() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/dst");
        setForm(data);
      } catch (e) {
        toast.error("Failed to load DST Gaming API config");
      }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/dst", {
        enabled: !!form.enabled,
        base_url: (form.base_url || "").trim(),
        company_code: (form.company_code || "").trim(),
        api_key: (form.api_key || "").trim(),
        callback_url: (form.callback_url || "").trim(),
        home_url: (form.home_url || "").trim(),
        currency: form.currency || "BDT",
        language: form.language || "en",
        platform: form.platform || "web",
        notes: form.notes || "",
      });
      setForm(data);
      toast.success("DST Gaming API configuration saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/dst/test");
      setTestResult(data);
      if (data.success) {
        toast.success(`DST reachable — ${data.provider_count} games in catalog`);
      } else {
        toast.error(data.error || data.response_msg || "DST declined the request");
      }
    } catch (e) {
      setTestResult({ success: false, error: e?.response?.data?.detail || e.message });
      toast.error("Test failed");
    } finally {
      setTesting(false);
    }
  };

  const suggestedCallback = (() => {
    if (typeof window === "undefined") return "";
    const proto = window.location.protocol;
    const host = window.location.host;
    return `${proto}//${host}/api/dstgaming/callback`;
  })();

  if (!form) {
    return (
      <div className="flex items-center justify-center h-96 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading DST Gaming API configuration…
      </div>
    );
  }

  return (
    <div style={{ background: SHELL_BG, minHeight: "100vh" }} className="p-6 text-gray-100">
      <PageHeader
        title="DST Gaming API Integration"
        subtitle="Configure DST Gaming ONE API credentials. This is the sole game-launch provider."
        icon={<Plug className="w-6 h-6" />}
      />

      {/* Master toggle */}
      <Section title="Master Switch" icon={<Zap className="w-4 h-4" />}>
        <Row
          label="Enable DST Gaming API"
          help="When enabled, all game launches route through DST Gaming (stagingapi.dstgaming.info / configured base URL)."
        >
          <Toggle value={!!form.enabled} onChange={(v) => set("enabled", v)} testId="dst-enable-toggle" />
        </Row>
      </Section>

      {/* Credentials */}
      <Section title="API Credentials" icon={<KeyRound className="w-4 h-4" />}>
        <Row label="Base URL" help="DST API base URL. Use staging (stagingapi.dstgaming.info) until you go live.">
          <Input
            value={form.base_url || ""}
            onChange={(v) => set("base_url", v)}
            placeholder="https://stagingapi.dstgaming.info"
            testId="dst-base-url"
          />
        </Row>
        <Row label="Company Code" help="Your DST companyCode (e.g. `oneapi`). Provided by DST when your account is created.">
          <Input
            value={form.company_code || ""}
            onChange={(v) => set("company_code", v)}
            placeholder="oneapi"
            testId="dst-company-code"
          />
        </Row>
        <Row label="API Key" help="Your DST apiKey (e.g. `EdNyNQ8Y`). Found in the DST merchant portal under API → Credentials.">
          <div className="flex gap-2">
            <Input
              value={form.api_key || ""}
              type={showKey ? "text" : "password"}
              onChange={(v) => set("api_key", v)}
              placeholder="EdNyNQ8Y"
              testId="dst-api-key"
              className="flex-1"
            />
            <IconBtn onClick={() => setShowKey(!showKey)} testId="dst-api-key-toggle">
              {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </IconBtn>
          </div>
        </Row>
      </Section>

      {/* URLs */}
      <Section title="URLs" icon={<Globe className="w-4 h-4" />}>
        <Row
          label="Seamless Callback URL"
          help="Set this single URL in your DST merchant portal. Our server routes getBalance / bet / settle / rollback / checkRefund automatically from the request body."
        >
          <div className="flex gap-2 items-stretch">
            <Input
              value={form.callback_url || ""}
              onChange={(v) => set("callback_url", v)}
              placeholder={suggestedCallback}
              testId="dst-callback-url"
              className="flex-1"
            />
            <IconBtn
              onClick={() => copyToClipboard(form.callback_url || suggestedCallback, "Callback URL copied")}
              testId="dst-callback-copy"
            >
              <CopyIcon className="w-4 h-4" />
            </IconBtn>
          </div>
          {!form.callback_url && (
            <button
              type="button"
              onClick={() => set("callback_url", suggestedCallback)}
              data-testid="dst-callback-suggest"
              className="text-xs mt-2 underline"
              style={{ color: ACCENT }}
            >
              Use this server&apos;s URL: {suggestedCallback}
            </button>
          )}
        </Row>

        <Row label="Home URL" help="Where the player is redirected when they close a game.">
          <Input
            value={form.home_url || ""}
            onChange={(v) => set("home_url", v)}
            placeholder="https://maxlive88.com"
            testId="dst-home-url"
          />
        </Row>
      </Section>

      {/* Locale */}
      <Section title="Defaults" icon={<Lock className="w-4 h-4" />}>
        <Row label="Currency Code" help="ISO currency code passed in launch payload.">
          <Input
            value={form.currency || ""}
            onChange={(v) => set("currency", v)}
            placeholder="BDT"
            testId="dst-currency"
          />
        </Row>
        <Row label="Language" help="ISO 639-1 language code (e.g. bn for Bengali, en for English).">
          <Input
            value={form.language || ""}
            onChange={(v) => set("language", v)}
            placeholder="en"
            testId="dst-language"
          />
        </Row>
        <Row label="Platform" help="Launch platform hint sent to DST (web, mobile, desktop).">
          <Select
            value={form.platform || "web"}
            onChange={(v) => set("platform", v)}
            options={[
              { value: "web", label: "Web (auto-detect)" },
              { value: "mobile", label: "Mobile" },
              { value: "desktop", label: "Desktop" },
            ]}
            testId="dst-platform"
          />
        </Row>
        <Row label="Notes (internal)" help="Free-form note shown only in admin.">
          <Input
            value={form.notes || ""}
            onChange={(v) => set("notes", v)}
            placeholder="Internal note…"
            testId="dst-notes"
          />
        </Row>
      </Section>

      {/* Migration banner */}
      <div
        className="mb-6 p-4 rounded-xl flex items-start gap-3"
        style={{ background: "#0d1f1a", border: `1px solid ${ACCENT}` }}
        data-testid="dst-migration-banner"
      >
        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} />
        <div className="text-sm text-gray-200">
          <div className="font-semibold mb-1" style={{ color: ACCENT }}>
            DST Gaming is the sole game-launch provider
          </div>
          <div className="text-gray-400 text-xs">
            All game launches route exclusively through DST Gaming ONE API. The legacy
            DST integration has been fully removed. Turnover, affiliate, referral,
            and VIP cashback hooks all fire on every DST bet/settle callback exactly
            as before.
          </div>
        </div>
      </div>

      {/* Actions */}
      <div
        className="flex gap-3 mt-6 sticky bottom-0 py-4"
        style={{ background: SHELL_BG }}
      >
        <button
          onClick={save}
          disabled={saving}
          data-testid="dst-save-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition disabled:opacity-50"
          style={{ background: ACCENT, color: "#04130a" }}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save changes
        </button>
        <button
          onClick={test}
          disabled={testing || !form.enabled || !form.api_key || !form.company_code}
          data-testid="dst-test-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition disabled:opacity-50"
          style={{ border: `1px solid ${PANEL_BORDER}`, color: "#cbd5e1", background: PANEL_BG }}
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          Test connection
        </button>
      </div>

      {/* Test result */}
      {testResult && (
        <div
          data-testid="dst-test-result"
          className="mt-4 p-4 rounded-lg"
          style={{
            background: PANEL_BG,
            border: `1px solid ${testResult.success ? "#16a34a" : "#dc2626"}`,
          }}
        >
          <div
            className="flex items-center gap-2 font-semibold mb-2"
            style={{ color: testResult.success ? "#22c55e" : "#ef4444" }}
          >
            {testResult.success ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
            {testResult.success ? "Connection OK" : "Connection failed"}
          </div>
          {testResult.error && <div className="text-sm text-red-300">{testResult.error}</div>}
          {testResult.response_msg && <div className="text-sm text-gray-300">{testResult.response_msg}</div>}
          {typeof testResult.provider_count !== "undefined" && (
            <div className="text-xs mt-2 text-gray-400">
              Games in EDP catalog probe: <span className="text-gray-200">{testResult.provider_count}</span>
            </div>
          )}
          {testResult.tested_url && (
            <div className="text-xs mt-1 break-all text-gray-400">
              Endpoint: <span className="text-gray-200">{testResult.tested_url}</span>
            </div>
          )}
          {typeof testResult.elapsed_ms !== "undefined" && (
            <div className="text-xs mt-1 text-gray-400">
              Latency: <span className="text-gray-200">{testResult.elapsed_ms} ms</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- helper components ---------------- */
const Section = ({ title, icon, children }) => (
  <div className="mb-6 rounded-xl" style={{ background: PANEL_BG, border: `1px solid ${PANEL_BORDER}` }}>
    <div
      className="px-5 py-3 border-b flex items-center gap-2"
      style={{ borderColor: PANEL_BORDER, color: ACCENT }}
    >
      {icon}
      <span className="text-sm font-semibold tracking-wide uppercase">{title}</span>
    </div>
    <div className="p-5 space-y-4">{children}</div>
  </div>
);

const Row = ({ label, help, children }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
    <div className="md:col-span-1">
      <div className="text-sm font-medium text-gray-200">{label}</div>
      {help && <div className="text-xs text-gray-500 mt-1">{help}</div>}
    </div>
    <div className="md:col-span-2">{children}</div>
  </div>
);

const Input = ({ value, onChange, placeholder, type = "text", testId, className = "" }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testId}
    className={`w-full px-3 py-2 rounded-lg outline-none transition ${className}`}
    style={{
      background: "#0d1521",
      border: `1px solid ${PANEL_BORDER}`,
      color: "#e2e8f0",
    }}
    onFocus={(e) => (e.target.style.borderColor = ACCENT)}
    onBlur={(e) => (e.target.style.borderColor = PANEL_BORDER)}
  />
);

const Select = ({ value, onChange, options, testId }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className="w-full px-3 py-2 rounded-lg outline-none transition"
    style={{
      background: "#0d1521",
      border: `1px solid ${PANEL_BORDER}`,
      color: "#e2e8f0",
    }}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
);

const IconBtn = ({ onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="px-3 rounded-lg transition hover:bg-white/5"
    style={{ background: "#0d1521", border: `1px solid ${PANEL_BORDER}`, color: "#94a3b8" }}
  >
    {children}
  </button>
);

const Toggle = ({ value, onChange, testId }) => (
  <button
    type="button"
    onClick={() => onChange(!value)}
    data-testid={testId}
    className="relative w-12 h-6 rounded-full transition"
    style={{ background: value ? ACCENT : "#1f2a3a" }}
  >
    <span
      className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
      style={{ left: value ? "calc(100% - 22px)" : "2px" }}
    />
  </button>
);
